import React from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, Clock, MapPin, User, Wrench, FileText, Info, CheckCircle, AlertCircle, Shield, ClipboardPlus } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function AgendamientoDetalle({ agendamiento, onClose, onEdit, onDelete, onCrearOrden }) {
  if (!agendamiento) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), "EEEE d 'de' MMMM, yyyy", { locale: es });
    } catch (e) {
      return dateStr;
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  const getEstadoColor = (estado) => {
    const map = {
      'pendiente': 'bg-yellow-100 text-yellow-800',
      'confirmado': 'bg-emerald-100 text-emerald-800',
      'en_progreso': 'bg-purple-100 text-purple-800',
      'completado': 'bg-green-100 text-green-800',
      'cancelado': 'bg-red-100 text-red-800'
    };
    return map[estado] || 'bg-gray-100 text-gray-800';
  };

  const getTipoColor = (tipo) => {
    const map = {
      'preventivo': 'text-green-600',
      'correctivo': 'text-amber-600',
      'revision': 'text-emerald-600'
    };
    return map[tipo] || 'text-gray-600';
  };

  const getPrioridadColor = (prioridad) => {
    const map = {
      'alta': 'bg-orange-100 text-orange-800 border-orange-200',
      'critica': 'bg-red-100 text-red-800 border-red-200',
      'media': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'baja': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return map[prioridad] || 'bg-gray-100 text-gray-800';
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="flex gap-2 mb-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getEstadoColor(agendamiento.estado)} capitalize`}>
                {agendamiento.estado?.replace('_', ' ')}
              </span>
              {agendamiento.prioridad && (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPrioridadColor(agendamiento.prioridad)} capitalize`}>
                  Prioridad {agendamiento.prioridad}
                </span>
              )}
            </div>
            <h2 className="text-xl font-bold text-gray-900">{agendamiento.titulo}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          
          {/* Main Info Blocks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Fecha y Hora */}
            <div className="flex items-start gap-3">
              <div className="mt-1 p-2 bg-teal-50 rounded-lg text-teal-600">
                <Calendar size={20} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Fecha Programada</p>
                <p className="text-gray-900 font-medium capitalize">{formatDate(agendamiento.fecha_programada)}</p>
                <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                  <Clock size={14} />
                  <span>{formatTime(agendamiento.hora_inicio)} - {formatTime(agendamiento.hora_fin)}</span>
                </div>
              </div>
            </div>

            {/* Ubicación */}
            <div className="flex items-start gap-3">
              <div className="mt-1 p-2 bg-red-50 rounded-lg text-red-600">
                <MapPin size={20} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Ubicación</p>
                <p className="text-gray-900 font-medium">{agendamiento.ubicacion || 'No especificada'}</p>
                {agendamiento.sucursal && (
                  <p className="text-sm text-gray-500">Sucursal: {agendamiento.sucursal}</p>
                )}
              </div>
            </div>

            {/* Cliente */}
            <div className="flex items-start gap-3">
              <div className="mt-1 p-2 bg-emerald-50 rounded-lg text-emerald-600">
                <User size={20} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Cliente</p>
                <p className="text-gray-900 font-medium">{agendamiento.cliente?.nombre || 'Sin Cliente'}</p>
              </div>
            </div>

            {/* Técnico */}
            <div className="flex items-start gap-3">
              <div className="mt-1 p-2 bg-purple-50 rounded-lg text-purple-600">
                <Wrench size={20} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Técnico Asignado</p>
                <p className="text-gray-900 font-medium">{agendamiento.tecnico_asignado || 'Sin Asignar'}</p>
              </div>
            </div>

          </div>

          <hr className="border-gray-100" />

          {/* Details */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2 mb-2">
                <FileText size={16} /> Detalles del Servicio
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Tipo de Mantenimiento</span>
                  <span className={`text-sm font-medium capitalize ${getTipoColor(agendamiento.tipo_mantenimiento)}`}>
                    {agendamiento.tipo_mantenimiento}
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-2">
                   <p className="text-sm text-gray-500 mb-1">Descripción</p>
                   <p className="text-gray-900 text-sm">{agendamiento.descripcion || 'Sin descripción'}</p>
                </div>
                {agendamiento.observaciones && (
                  <div className="border-t border-gray-200 pt-2">
                    <p className="text-sm text-gray-500 mb-1">Observaciones</p>
                    <p className="text-gray-700 text-sm italic">{agendamiento.observaciones}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Equipos */}
            {agendamiento.equipos && agendamiento.equipos.length > 0 && (
              <div>
                 <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2 mb-2">
                  <Shield size={16} /> Equipos ({agendamiento.equipos.length})
                </h3>
                <div className="grid grid-cols-1 gap-2">
                  {agendamiento.equipos.map((eq, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg text-sm">
                      <span className="font-medium text-gray-900">{eq.nombre}</span>
                      <span className="text-gray-500">{eq.marca} {eq.modelo}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Metadata Footer */}
          <div className="bg-gray-50 -mx-6 -mb-6 px-6 py-4 mt-6 text-xs text-gray-400 flex justify-between items-center border-t border-gray-100">
            <div>
              <p>ID: {agendamiento.id || agendamiento._id}</p>
              <p>Creado el {formatDate(agendamiento.created_at)}</p>
            </div>
            <div className="text-right">
              <p>Creado por:</p>
              <p className="font-medium text-gray-600">{agendamiento.created_by_name || 'Sistema'}</p>
            </div>
          </div>

        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 flex justify-between items-center rounded-b-xl">
           <button 
            onClick={() => onDelete(agendamiento)}
            className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
           >
            Eliminar
           </button>
           <div className="flex gap-3">
             {onCrearOrden && ['pendiente', 'confirmado', 'programado'].includes(agendamiento.estado) && (
               <button 
                onClick={() => onCrearOrden(agendamiento)}
                className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg text-sm font-medium shadow-sm transition-colors flex items-center gap-2"
               >
                <ClipboardPlus size={16} />
                Crear Orden de Servicio
               </button>
             )}
             <button 
              onClick={() => onEdit(agendamiento)}
              className="px-4 py-2 bg-teal-600 text-white hover:bg-teal-700 rounded-lg text-sm font-medium shadow-sm transition-colors"
             >
              Editar Agendamiento
             </button>
           </div>
        </div>

      </div>
    </div>,
    document.body
  );
}